// ════════════════════════════════════════════════════════════════════════════
//  ManDana — Omana People
//  ---------------------------------------------------------------------------
//  The second database ManDana ships, and the first whose rows are not the
//  account's own. It is the people this user vouched for: everyone they wrote
//  down, the username each one was given, and whether they have arrived yet.
//
//  THE ROWS ARE A PROJECTION OF A SERVER TABLE, and every rule in this file
//  falls out of that one fact.
//
//  A username has to be unique for EVERYBODY — two people cannot be told they
//  are `marwan.galaly.1`, because a username is what you sign in with. So the
//  registry is a table on the server and the counter is the database's to give
//  (migrations/0007_omana_registry.sql). This device only ever reads it.
//
//  WHICH IS WHY THE CACHE IS NEVER SYNCED. There is no mdOmanaSyncDocs here and
//  there never will be: a roster pushed from one device and merged on another is
//  two devices arguing, last-writer-wins, about a fact neither of them owns —
//  and the loser of that argument is a row that says somebody has not activated
//  their account when they have. `md:omana` is written from one place, the
//  server's answer, and is thrown away and refetched rather than reconciled.
//
//  AND WHY NOTHING HERE CAN BE EDITED. There is no document under a row to
//  write to. md-database.ts refuses the row add, the delete and the cell — the
//  same refusals the Main DB already earns — so a surface can ask
//  `mdDatabaseBuiltin` and decline to draw a command the door would refuse.
//  Adding a person is a form that posts to the registry; that is OM-9's story
//  and it is deliberately not a row add wearing a costume.
//
//  OFFLINE IT RENDERS THE LAST ROSTER IT SAW. The people you vouched for did not
//  stop existing because the plane took off, so a refusal — no network, no
//  session, a 500 — leaves the cache exactly as it was rather than clearing it.
//  An empty roster is a statement, and it is not one a failed fetch may make.
//
//  THE ROSTER CROSSED THE NETWORK, so it is sanitised on the way in like any
//  other untrusted document: an id that could never be a row id is dropped
//  rather than silently replaced (md-database.ts holds row ids to
//  `[a-z0-9]{6,40}` and a replaced id would stop matching the registry), a
//  username that is not shaped like one is dropped entirely — a username we
//  cannot render is one we must never offer somebody to share — and a status or
//  a gender the app does not know reads as the safe answer rather than the
//  server's word for it.
//
//  ONE AUTHORITY FOR THE USERNAME, and it is the server. This file could
//  recompute it from the name and the number; it deliberately does not. Two
//  spellings of the slug rule is one spelling too many, and the day they diverge
//  is the day a user shares an address that does not exist.
//
//  EVERY ID HERE CLEARS SIX CHARACTERS. `accountstatus` rather than `status`,
//  `genderfemale`/`gendermale` rather than `female`/`male`, for the reason
//  md-librarian.ts writes `rectype` rather than `type`: md-database.ts silently
//  replaces a shorter id with a generated one, the column still renders, and
//  nothing can ever name it again.
//
//  HEADLESS, like the librarian. No DOM, no React, no store held in state — the
//  gallery is one consumer of `mdOmanaRows`, and the confirmation panel that
//  re-shares an invitation is another.
// ════════════════════════════════════════════════════════════════════════════

(function (g: any) {
  'use strict';

  /** Well-known and permanent: eleven lowercase letters, so it satisfies the
      same `[a-z0-9]{6,40}` grammar every user-made database is held to, and no
      generated id (`d` + random) can collide with it. */
  const ID = 'omanapeople';
  const NAME = 'Omana People';

  /** The roster, as the server last told it. Written from exactly one place —
      applyRoster — and never merged. See the header. */
  const CACHE = 'md:omana';

  /* `title` is the id, not a choice: the gallery card reads `row.values.title`
      by name (md-database.jsx's DbGallery), and so does every other view. The
      other three clear the six-character floor on their own. */
  const TITLE = 'title';
  const USERNAME = 'username';
  const STATUS = 'accountstatus';
  const GENDER = 'gender';

  const NOT_ACTIVATED = 'notactivated';
  const ACTIVATED = 'activated';
  /** Prefixed for the floor's sake — `male` is four characters. */
  const GENDER_OPTION: Record<string, string> = { female: 'genderfemale', male: 'gendermale' };

  const MAX_ROSTER = 500;
  const MAX_NAME = 40;

  /* What the server mints, and the only shape this file will render. Three
     lowercase-or-digit segments and a number, on the one domain. A username
     that does not match is not displayed at all: see the header. */
  const USERNAME_RE = /^[a-z0-9]+\.[a-z0-9]+\.[1-9][0-9]{0,8}@omana\.network$/;
  const ROW_ID_RE = /^[a-z0-9]{6,40}$/;

  type OmanaPerson = {
    id: string; first: string; last: string; n: number;
    gender: string; username: string; status: string; created_at: number;
  };

  const tidy = (value: unknown): string =>
    typeof value === 'string' ? value.replace(/\s+/g, ' ').trim().slice(0, MAX_NAME) : '';

  /** The database as shipped. Returned only for this id, and only while no
      document is stored under it — the moment the user changes a view,
      md-database.ts saves a real document and reads that from then on. Partial
      on purpose: the database sanitiser fills in every field a view needs, so
      this file carries no copy of the view shape to drift out of date. */
  function mdOmanaDatabase(dbId?: unknown): Record<string, unknown> | null {
    if (dbId !== undefined && dbId !== ID) return null;
    return {
      version: 3, id: ID, name: NAME,
      properties: [
        { id: TITLE, name: 'Name', type: 'title' },
        /* Plain text rather than an email type: it is an address on a namespace
           that serves nothing yet, and offering to open a mail client for it
           would be the app promising something it cannot do. */
        { id: USERNAME, name: 'Username', type: 'text' },
        /* A `select`, so the two states are words the reader knows rather than
           whatever the wire happened to say, and so they can be grouped and
           filtered on later without a migration. Not activated first: it is the
           state a person is in when the card is worth looking at. */
        { id: STATUS, name: 'Account Status', type: 'select',
          options: [
            { id: NOT_ACTIVATED, name: 'Not Activated', color: 'amber' },
            { id: ACTIVATED, name: 'Activated', color: 'mint' },
          ] },
        /* Chosen by whoever added the person, so the invited person is never
           asked for it on their way in. */
        { id: GENDER, name: 'Gender', type: 'select',
          options: [
            { id: GENDER_OPTION.female, name: 'Female' },
            { id: GENDER_OPTION.male, name: 'Male' },
          ] },
      ],
      rows: [],
      /* A gallery, because these are people rather than line items — and the
         card is where a name, an address and a status read as one thing. The
         username is the card's own second line (see mdOmanaRows) rather than a
         meta chip, so it is deliberately not in the visible list: it would
         otherwise be printed twice on every card. */
      views: [{ id: 'omanagallery', name: 'My network', type: 'gallery',
        visiblePropertyIds: [TITLE, STATUS, GENDER] }],
      activeViewId: 'omanagallery',
      locked: false, templates: [], automations: [],
      /* Shipped, not created. A zero says "the app has always had this" rather
         than inventing a moment the user was not there for. */
      created: 0, updated: 0, revision: 1,
    };
  }

  /* ── the cache ───────────────────────────────────────────────────────────── */

  function cached(): { at: number; people: OmanaPerson[] } {
    const raw = g.mdRead(CACHE, null);
    if (!raw || typeof raw !== 'object' || !Array.isArray(raw.people)) return { at: 0, people: [] };
    return { at: Number.isFinite(raw.at) ? Number(raw.at) : 0, people: raw.people as OmanaPerson[] };
  }

  /** One person, sanitised, or null for one this device will not render. */
  function person(raw: unknown): OmanaPerson | null {
    if (!raw || typeof raw !== 'object') return null;
    const item = raw as Record<string, unknown>;
    const id = typeof item.id === 'string' && ROW_ID_RE.test(item.id) ? item.id : '';
    const username = typeof item.username === 'string' && USERNAME_RE.test(item.username) ? item.username : '';
    const first = tidy(item.first);
    const last = tidy(item.last);
    /* All three or nothing. A row with no id cannot be found again, a person
       with no name cannot be greeted, and a username we cannot render is one we
       must never offer somebody to share. */
    if (!id || !username || !(first || last)) return null;
    const n = Number(item.n);
    return {
      id, first, last,
      n: Number.isInteger(n) && n >= 1 ? n : 1,
      gender: typeof item.gender === 'string' && GENDER_OPTION[item.gender] ? item.gender : '',
      username,
      // Anything that is not the word for arrived reads as not arrived.
      status: item.status === 'activated' ? ACTIVATED : NOT_ACTIVATED,
      created_at: Number.isFinite(item.created_at) ? Number(item.created_at) : 0,
    };
  }

  /** Replace the roster with what the server said. Returns false for anything
      that is not a list of people — including a `{people:[…]}` envelope, which
      is the caller handing over the response instead of its contents. */
  function mdOmanaApplyRoster(people: unknown): boolean {
    if (!Array.isArray(people)) return false;
    const out: OmanaPerson[] = [], seen = new Set<string>();
    for (const raw of people.slice(0, MAX_ROSTER)) {
      const one = person(raw);
      if (!one || seen.has(one.id)) continue;    // first writing of an id wins
      seen.add(one.id);
      out.push(one);
    }
    return g.mdWrite(CACHE, { v: 1, at: Date.now(), people: out }) !== false;
  }

  /** Ask the server, then apply. A refusal leaves the cache alone — see the
      header. Never throws: the verb it calls reports unreachable rather than
      rejecting (md-sync.js). */
  async function mdOmanaRefresh(): Promise<{ ok: boolean; count?: number; error?: string }> {
    if (typeof g.mdOmanaPeople !== 'function') return { ok: false, error: 'unavailable' };
    const res = await g.mdOmanaPeople();
    if (!res || !res.ok) return { ok: false, error: (res && res.error) || 'failed' };
    if (!mdOmanaApplyRoster(res.people)) return { ok: false, error: 'not_written' };
    return { ok: true, count: cached().people.length };
  }

  /* ── the rows ────────────────────────────────────────────────────────────── */

  /** Every person, as a row the table and the gallery can draw. Composed on
      every read: nothing here is stored as a row, so a roster that arrives is
      on screen with no bookkeeping in between. */
  function mdOmanaRows(): any[] {
    return cached().people.map((one) => ({
      id: one.id,
      values: {
        [TITLE]: `${one.first} ${one.last}`.trim(),
        [USERNAME]: one.username,
        [STATUS]: one.status,
        [GENDER]: GENDER_OPTION[one.gender] || null,
      },
      /* The card's second line. Left empty, DbGallery falls back to "A quiet
         record ready for detail." — a sentence about a note, printed under
         somebody's name. */
      body: one.username,
      /* Nothing here can be written to. These rows are composed from the cache
         on every read and the registry is the server's, so every cell edit is
         refused at the door — a table view must draw them as text rather than
         as inputs that accept a name and then drop it. */
      readOnly: true,
      created: one.created_at,
      updated: one.created_at,
    }));
  }

  /** The person behind a card, for the panel that re-shares their invitation.
      Read from the cache rather than from the row, because the share message
      greets somebody by their FIRST name and a row only knows the whole one. */
  function mdOmanaPerson(rowId: unknown): OmanaPerson | null {
    if (typeof rowId !== 'string') return null;
    return cached().people.find((one) => one.id === rowId) || null;
  }

  /** When the roster in hand was fetched, or 0. The surface says "as of" with
      it rather than presenting a cached roster as this second's truth. */
  function mdOmanaFetchedAt(): number { return cached().at; }

  Object.assign(g, {
    MD_OMANA_ID: ID,
    MD_OMANA_CACHE: CACHE,
    mdOmanaDatabase, mdOmanaRows, mdOmanaPerson, mdOmanaApplyRoster,
    mdOmanaRefresh, mdOmanaFetchedAt,
  });

  // CommonJS escape hatch, following md-librarian.ts.
  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = { MD_OMANA_ID: ID, MD_OMANA_CACHE: CACHE, mdOmanaDatabase,
      mdOmanaRows, mdOmanaPerson, mdOmanaApplyRoster, mdOmanaRefresh, mdOmanaFetchedAt };
  }
})(typeof window !== 'undefined' ? window : globalThis);
